import { useState } from 'react'
import {
  ArrowLeft, ListTree, X, LayoutTemplate,
  Image, Images, Columns, AlignLeft, Heading2, List, Bookmark, BookOpen, Package, Video, Volume2,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type { WireArticleBlock, WireArticleFull } from '../../api/types'

// ─── Block palette data ───────────────────────────────────────────────────────

type PaletteBlockDef = {
  type: string
  name: string
  description: string
  icon: React.ComponentType<{ size?: number; className?: string }>
}

const PALETTE_GROUPS: { label: string; blocks: PaletteBlockDef[] }[] = [
  {
    label: 'Media',
    blocks: [
      { type: 'photo',            name: 'Photo',            description: 'Single image with caption',         icon: Image },
      { type: 'photo-group',      name: 'Photo Group',      description: 'Grid or carousel of photos',        icon: Images },
      { type: 'photo-comparison', name: 'Photo Comparison', description: 'Before / after drag slider',        icon: Columns },
      { type: 'video',            name: 'Video',            description: 'Uploaded video with playback',      icon: Video },
      { type: 'audio',            name: 'Audio',            description: 'Uploaded audio with transcript',    icon: Volume2 },
      { type: 'visual',           name: 'Visual',           description: '.visual interactive or graphic',    icon: Package },
    ],
  },
  {
    label: 'Text',
    blocks: [
      { type: 'paragraph', name: 'Paragraph', description: 'Body text paragraph',         icon: AlignLeft },
      { type: 'heading',   name: 'Heading',   description: 'Section heading (H2)',         icon: Heading2 },
      { type: 'list',      name: 'List',      description: 'Bulleted or numbered list',    icon: List },
    ],
  },
  {
    label: 'Structure',
    blocks: [
      { type: 'section-marker', name: 'Section',     description: 'Summary, scope note, etc.', icon: Bookmark },
      { type: 'source-list',    name: 'Source List', description: 'Citations and references',  icon: BookOpen },
    ],
  },
]

function PaletteCard({ block, onInsert, onDragStart, onDragEnd }: {
  block: PaletteBlockDef
  onInsert: (type: string) => void
  onDragStart: () => void
  onDragEnd: () => void
}) {
  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('application/x-wire-block-type', block.type)
        e.dataTransfer.effectAllowed = 'copy'
        onDragStart()
      }}
      onDragEnd={onDragEnd}
      onClick={() => onInsert(block.type)}
      className="flex cursor-grab items-center gap-2.5 rounded px-2 py-1.5 transition-colors hover:bg-fill active:cursor-grabbing"
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onInsert(block.type) }}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-line bg-canvas text-fg-secondary">
        <block.icon size={13} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-fg">{block.name}</p>
        <p className="text-[10px] text-fg-secondary">{block.description}</p>
      </div>
    </div>
  )
}

// ─── Section type labels ──────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  'summary':            'Key Judgments',
  'scope-note':         'Scope Note',
  'methodology-note':   'Methodology Note',
  'tradecraft-summary': 'Tradecraft Summary',
  'for-additional-info':'For Additional Info',
  'appendix':           'Appendix',
}

// ─── ToC data model ───────────────────────────────────────────────────────────

interface TocHeading {
  block: Extract<WireArticleBlock, { type: 'heading' }>
}

interface TocSection {
  block: Extract<WireArticleBlock, { type: 'section-marker' }>
  label: string
  headings: TocHeading[]
}

/** Walk blocks in order. Group headings under their preceding section-marker. */
function buildToc(blocks: WireArticleBlock[]): TocSection[] {
  const toc: TocSection[] = []
  let current: TocSection | null = null

  for (const block of blocks) {
    if (block.type === 'section-marker') {
      current = {
        block,
        label: SECTION_LABELS[block.sectionType] ?? block.sectionType.replace(/-/g, ' '),
        headings: [],
      }
      toc.push(current)
    } else if (block.type === 'heading' && current) {
      current.headings.push({ block })
    }
  }

  return toc
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  article: WireArticleFull
  activeSectionId: string | null
  tocOpen: boolean
  onTocToggle: () => void
  onSectionClick: (blockId: string) => void
  blockPaletteOpen: boolean
  onBlockPaletteToggle: () => void
  onInsertBlock: (blockType: string) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ArticleTocShelf({ article, activeSectionId, tocOpen, onTocToggle, onSectionClick, blockPaletteOpen, onBlockPaletteToggle, onInsertBlock }: Props) {
  const [draggingFromPalette, setDraggingFromPalette] = useState(false)
  const classStr = article.classification
    ? `(${[article.classification, ...(article.disseminationControls ?? [])].join('//')})`
    : null

  const toc = buildToc(article.blocks)

  return (
    <>
      {/* ── Narrow icon rail (always visible) ────────────────────────────── */}
      <aside className="flex w-10 shrink-0 flex-col items-center gap-3 border-r border-line bg-shelf py-3">

        {/* Back to articles */}
        <Link
          to="/admin/articles"
          className="flex h-7 w-7 items-center justify-center rounded text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          aria-label="Back to articles"
          title="Back to articles"
        >
          <ArrowLeft size={15} aria-hidden="true" />
        </Link>

        {/* ToC toggle */}
        <button
          type="button"
          onClick={onTocToggle}
          aria-label="Toggle table of contents"
          aria-expanded={tocOpen}
          title="Table of contents"
          className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
            tocOpen
              ? 'bg-accent-primary-fill text-accent-primary-fg'
              : 'text-fg-secondary hover:bg-fill hover:text-fg'
          }`}
        >
          <ListTree size={15} aria-hidden="true" />
        </button>

        {/* Block palette toggle */}
        <button
          type="button"
          onClick={onBlockPaletteToggle}
          aria-label="Add block"
          aria-expanded={blockPaletteOpen}
          title="Add block"
          className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
            blockPaletteOpen
              ? 'bg-accent-primary-fill text-accent-primary-fg'
              : 'text-fg-secondary hover:bg-fill hover:text-fg'
          }`}
        >
          <LayoutTemplate size={15} aria-hidden="true" />
        </button>

      </aside>

      {/* ── Overlay ToC panel (slides in over the canvas) ─────────────────── */}
      {tocOpen && (
        <>
          {/* Backdrop — click outside to dismiss */}
          <div
            className="absolute inset-0 z-10"
            style={{ top: '3rem' }}
            onClick={onTocToggle}
            aria-hidden="true"
          />

          <div className="absolute left-10 z-20 flex w-64 flex-col border-r border-line bg-shelf shadow-xl"
            style={{ top: '3rem', bottom: 0 }}
          >
            {/* Panel header */}
            <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
              <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Contents</p>
              <button
                type="button"
                onClick={onTocToggle}
                className="rounded p-0.5 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                aria-label="Close table of contents"
              >
                <X size={13} />
              </button>
            </div>

            {/* Article classification + title */}
            <div className="shrink-0 px-4 py-3">
              {classStr && (
                <p className="mb-1 font-mono text-[10px] font-bold text-teal-11">
                  {classStr}
                </p>
              )}
              <p className="line-clamp-3 text-[11px] font-semibold leading-snug text-fg">
                {article.title}
              </p>
            </div>

            <div className="mx-4 shrink-0 border-t border-line" />

            {/* ToC nav */}
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {toc.length > 0 ? (
                <div className="space-y-0.5">
                  {toc.map(({ block, label, headings }) => {
                    const isActive = block.id === activeSectionId
                    return (
                      <div key={block.id}>
                        <button
                          type="button"
                          onClick={() => { onSectionClick(block.id); onTocToggle() }}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-colors ${
                            isActive
                              ? 'bg-accent-primary-fill font-semibold text-accent-primary-fg'
                              : 'text-fg-secondary hover:bg-fill hover:text-fg'
                          }`}
                        >
                          {label}
                        </button>
                        {headings.length > 0 && (
                          <div className="ml-3 mt-0.5 space-y-0.5 border-l border-line pl-2">
                            {headings.map(({ block: hBlock }) => {
                              const isHeadingActive = hBlock.id === activeSectionId
                              return (
                                <button
                                  key={hBlock.id}
                                  type="button"
                                  onClick={() => { onSectionClick(hBlock.id); onTocToggle() }}
                                  className={`w-full rounded px-2 py-1 text-left text-[11px] leading-snug transition-colors ${
                                    isHeadingActive
                                      ? 'bg-accent-primary-fill font-semibold text-accent-primary-fg'
                                      : 'text-fg-secondary hover:bg-fill hover:text-fg'
                                  }`}
                                >
                                  {hBlock.content}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-[11px] italic text-fg-secondary">No sections found.</p>
              )}
            </nav>
          </div>
        </>
      )}

      {/* ── Block palette overlay ─────────────────────────────────────────── */}
      {blockPaletteOpen && (
        <>
          <div
            className={`absolute inset-0 z-10 ${draggingFromPalette ? 'pointer-events-none' : ''}`}
            style={{ top: '3rem' }}
            onClick={onBlockPaletteToggle}
            aria-hidden="true"
          />
          <div
            className="absolute left-10 z-20 flex w-64 flex-col border-r border-line bg-shelf shadow-xl"
            style={{ top: '3rem', bottom: 0 }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
              <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Add Block</p>
              <button
                type="button"
                onClick={onBlockPaletteToggle}
                className="rounded p-0.5 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
                aria-label="Close block palette"
              >
                <X size={13} />
              </button>
            </div>

            {/* Hint */}
            <p className="shrink-0 px-4 pt-3 text-[10px] text-fg-secondary">
              Click to append · drag onto the article to place
            </p>

            {/* Groups */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
              {PALETTE_GROUPS.map(group => (
                <div key={group.label} className="mb-4">
                  <p className="mb-1 px-2 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.blocks.map(block => (
                      <PaletteCard
                        key={block.type}
                        block={block}
                        onInsert={onInsertBlock}
                        onDragStart={() => setDraggingFromPalette(true)}
                        onDragEnd={() => { setDraggingFromPalette(false); onBlockPaletteToggle() }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}
